import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  RowDataUpdatedEvent,
  RowDragEndEvent,
} from 'ag-grid-community'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { useGridContext } from '../../containers/BulkSheetView/hooks/useGridContext'
import { useDragStyle } from '../../containers/BulkSheetView/hooks/gridEvents/rowDrag'
import { getSelectedNode } from '../../containers/BulkSheetView/lib/gridApi'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { ROW_HEIGHT } from '../../containers/BulkSheet'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useSubStatusBulkSheetState } from './hooks/useSubStatusBulkSheetState'
import Loading from '../../components/process-state-notifications/Loading'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
import { useProjectPrivateContext } from '../../context/projectContext'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { requireSave, doNotRequireSave } from '../../../store/requiredSaveData'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { usePageState } from '../../hooks/usePageState'
import SubStatusHeader from './components/Header'
import { useSubStatusGridOptions } from './SubStatusOptions'
import { useSubStatusData } from './hooks/useSubStatusData'
import { createContextMenuItems } from './lib/gridUtils'
import {
  SubStatusRow,
  createNewSubStatusRow,
  isDeletableSubStatusRow,
} from './SubStatusRow'
import { addScreenMessage, MessageLevel } from '../../../store/messages'
import {
  CustomEnumCode,
  getCustomEnumValues,
} from '../../../lib/functions/customEnumValue'
import AddRowCountInputDialog, {
  AddRowCountInputDialogState,
} from '../../containers/BulkSheet/AddRowCountInputDialog'
import { intl } from '../../../i18n'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'

// 開発・Staging限定のサブステータス追加プロトタイプ画面。
// functionsマスタ(application_functions)に登録済み(external_id: project.subStatus.edit)。
// 表示制御は feature-flag "sub-status"(view/pages/index.tsx)。
type SubStatusPageState = {
  wbsItemType: string
}

const defaultPageState: SubStatusPageState = { wbsItemType: '' }

type Props = PageProps & {
  dispatch: any
}

const SubStatusComponent = projectPrivate((props: Props) => {
  const { uuid: functionUuid, dispatch } = props
  const { projectUuid, wbsItemTypes, ticketTypes } = useProjectPrivateContext()
  const { wbsItemType, updatePageState } = usePageState<SubStatusPageState>(
    functionUuid,
    defaultPageState,
    s => s,
    projectUuid
  )
  const handleChangeWbsItemType = useCallback(
    (code: string) => updatePageState({ wbsItemType: code }),
    [updatePageState]
  )
  // サブステータスを設定できるのは、実際にサブステータス項目が表示される単位である
  // 成果物・タスクと、タスクを細分化したチケットタイプ。
  const selectableWbsItemTypes = useMemo(
    () =>
      [wbsItemTypes?.deliverable, wbsItemTypes?.task, ...ticketTypes].filter(
        (type): type is WbsItemTypeVO => !!type
      ),
    [wbsItemTypes, ticketTypes]
  )
  const ref = useRef<HTMLDivElement>(null)
  const gridApiRef = useRef<GridApi>()
  // gridApiRefはrefなのでセットしただけでは再レンダーされない。
  // useGridContextにgridApiRef.currentを正しく渡すため、取得時に明示的に再レンダーを起こす
  const [isGridReady, setIsGridReady] = useState(false)

  const {
    rowData,
    hasUnsavedChanges,
    loading,
    handleReload,
    handleSubmit,
    handleDataChange,
  } = useSubStatusData({
    wbsItemType,
    functionUuid,
    gridApi: gridApiRef.current,
  })

  useEffect(() => {
    if (hasUnsavedChanges) {
      dispatch(requireSave())
    } else {
      dispatch(doNotRequireSave())
    }
  }, [hasUnsavedChanges, dispatch])

  const handleClickAddRow = useCallback(() => {
    handleDataChange([...rowData, createNewSubStatusRow()])
  }, [rowData, handleDataChange])

  // リロード(更新)ボタン: 未保存の変更があれば取り消し確認ダイアログを出してから初期化する。
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const onReload = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true)
    } else {
      handleReload()
    }
  }, [hasUnsavedChanges, handleReload])

  // 行数を指定してまとめて行を追加するダイアログの状態
  const [addRowCountDialogState, setAddRowCountDialogState] =
    useState<AddRowCountInputDialogState>({ open: false })

  const handleOpenAddMultipleRows = useCallback(() => {
    setAddRowCountDialogState({
      open: true,
      title: intl.formatMessage({
        id: 'bulksheet.contextMenu.insert.multipleRow.title',
      }),
      submitHandler: (addRowCount: number) => {
        const newRows = Array.from({ length: addRowCount }, () =>
          createNewSubStatusRow()
        )
        handleDataChange([...rowData, ...newRows])
        setAddRowCountDialogState({ open: false })
      },
      closeHandler: () => setAddRowCountDialogState({ open: false }),
    })
  }, [rowData, handleDataChange])
  const handleOpenAddMultipleRowsRef = useRef(handleOpenAddMultipleRows)
  handleOpenAddMultipleRowsRef.current = handleOpenAddMultipleRows

  // 削除確認ダイアログの状態。対象は開いた時点の選択行で確定させ、
  // ダイアログ表示中に選択が変わっても消す行がずれないようにする。
  const [deleteTarget, setDeleteTarget] = useState<
    { uuids: string[]; names: string[] } | undefined
  >(undefined)

  // 選択中の行の削除を要求する。プリセット由来かどうかによらず削除できるが、
  // 使用中(WBSアイテムに設定済み)の行は参照先が失われるため削除させない。
  const handleRequestDeleteSelectedRows = useCallback(() => {
    const selectedNodes = getSelectedNode(gridApiRef.current as GridApi)
    if (selectedNodes.length === 0) return

    const usedRowNames = selectedNodes
      .filter(node => !isDeletableSubStatusRow(node.data))
      .map(node => node.data?.name)
      .filter(Boolean)
    // コンテキストメニューは非活性にしているが、ショートカットからも到達するのでここでも弾く
    if (usedRowNames.length > 0) {
      dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage(
            { id: 'subStatus.validation.usedCannotDelete' },
            { names: usedRowNames.join(', ') }
          ),
        })
      )
      return
    }

    // ag-gridのノードはdataを持たないことがあるため、行データのあるものだけを対象にする
    const targetRows = selectedNodes.flatMap(node => node.data ?? [])
    if (targetRows.length === 0) return
    setDeleteTarget({
      uuids: targetRows.map(row => row.uuid),
      names: targetRows.map(
        row => row.name || intl.formatMessage({ id: 'displayName.undefined' })
      ),
    })
  }, [dispatch, functionUuid])
  const handleRequestDeleteSelectedRowsRef = useRef(
    handleRequestDeleteSelectedRows
  )
  handleRequestDeleteSelectedRowsRef.current = handleRequestDeleteSelectedRows

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return
    handleDataChange(rowData.filter(r => !deleteTarget.uuids.includes(r.uuid)))
    setDeleteTarget(undefined)
  }, [deleteTarget, rowData, handleDataChange])

  const rowDataRef = useRef(rowData)
  rowDataRef.current = rowData
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) =>
      createContextMenuItems(
        params,
        rowDataRef.current,
        handleDataChange,
        () => handleRequestDeleteSelectedRowsRef.current(),
        () => handleOpenAddMultipleRowsRef.current()
      ),
    [handleDataChange]
  )

  const gridOptionsFromHook = useSubStatusGridOptions()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 列定義はマウント後に固定。statusOptionsの反映はupdateContext経由で行う
  const gridOptions = useMemo(() => gridOptionsFromHook, [])
  const { updateContext } = useGridContext(
    gridApiRef.current,
    gridOptions.context
  )

  // ヘッダーツールバー(フィルタ / ソート列 / 列設定)の状態。列・フィルタ状態は UiState に永続化する。
  const bulkSheetState = useSubStatusBulkSheetState(
    gridApiRef.current,
    functionUuid,
    projectUuid
  )
  const columnSetting = useColumnSetting()

  // 行の高さ(表示形式)。変更したら既存行にも反映されるよう resetRowHeights する。
  const [rowHeight, setRowHeight] = useState<number>(ROW_HEIGHT.SMALL)
  useEffect(() => {
    if (!isGridReady) return
    gridApiRef.current?.resetRowHeights()
  }, [rowHeight, isGridReady])

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api
    setIsGridReady(true)
  }, [])

  useEffect(() => {
    // isGridReady(=gridApi取得済み)になるまでは、古いgridApiを掴んだupdateContextで
    // 実際のgrid contextに反映されないため待つ
    if (!isGridReady) return
    getCustomEnumValues({
      customEnumCode: CustomEnumCode.WBS_STATUS,
      groupUuid: projectUuid,
    }).then(response => updateContext({ statusOptions: response.json ?? [] }))
  }, [projectUuid, isGridReady, updateContext])

  // 同じ名前(前後空白・大文字小文字を無視)が2件以上ある場合、それらをエラー表示対象とする。
  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>()
    rowData.forEach(row => {
      const name = row.name.trim().toLowerCase()
      if (!name) return
      counts.set(name, (counts.get(name) ?? 0) + 1)
    })
    return new Set(
      [...counts.entries()]
        .filter(([, count]) => count > 1)
        .map(([name]) => name)
    )
  }, [rowData])

  useEffect(() => {
    if (!isGridReady) return
    updateContext({ duplicateNames })
    // cellStyleはcontext変更だけでは再評価されないため、明示的にセルを再描画する
    gridApiRef.current?.refreshCells({ force: true })
  }, [duplicateNames, isGridReady, updateContext])

  const dragStyle = useDragStyle(gridApiRef.current, updateContext)

  // ドラッグ&ドロップで並べ替える。プリセット・カスタムを問わず任意の位置へ移動できる。
  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent<SubStatusRow>) => {
      try {
        const movingData = event.node.data
        if (!movingData) return
        const overIndex = event.overIndex
        if (overIndex === undefined || overIndex < 0) return

        const newData = rowData.filter(row => row.uuid !== movingData.uuid)
        newData.splice(overIndex, 0, movingData)
        handleDataChange(newData)
      } finally {
        dragStyle.refreshDragStyle()
      }
    },
    [rowData, handleDataChange, dragStyle]
  )

  // キーボードショートカット: Ctrl+S で保存、Alt+Shift+D で選択行の削除
  // (Alt+Shift+D はコンテキストメニューにショートカットとして表示している操作)
  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: handleSubmit,
        stopDefaultBehavior: true,
      },
      {
        key: 'alt+shift+d',
        fn: handleRequestDeleteSelectedRows,
      },
    ],
    [handleSubmit, handleRequestDeleteSelectedRows]
  )

  return (
    <PageArea>
      <SavePopper loading={loading} onSubmit={handleSubmit} />
      <SubStatusHeader
        wbsItemType={wbsItemType}
        wbsItemTypes={selectableWbsItemTypes}
        onChangeWbsItemType={handleChangeWbsItemType}
        onClickAddRow={handleClickAddRow}
        disableAddRow={!wbsItemType}
        onReload={onReload}
        filteredColumns={bulkSheetState.filteredColumns}
        onDeleteFilteredColumn={bulkSheetState.onDeleteFilteredColumn}
        resetFilters={bulkSheetState.resetFilters}
        sortColumnsState={bulkSheetState.sortColumnsState}
        onDeleteSortedColumn={bulkSheetState.onDeleteSortedColumn}
        onDeleteSortedAllColumns={bulkSheetState.onDeleteSortedAllColumns}
        onChangeSortColumnState={bulkSheetState.onChangeSortColumnState}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={rowHeight}
        onChangeRowHeight={setRowHeight}
      />
      <BulkSheetView
        ref={ref}
        gridOptions={gridOptions}
        rowData={rowData}
        rowHeight={rowHeight}
        onGridReady={handleGridReady}
        onRowDataUpdated={(e: RowDataUpdatedEvent<SubStatusRow>) => {
          if (e.api && gridApiRef.current !== e.api) {
            gridApiRef.current = e.api
            setIsGridReady(true)
          }
        }}
        onFirstDataRendered={bulkSheetState.onFirstDataRendered}
        onSortChanged={bulkSheetState.onSortChanged}
        onColumnMoved={bulkSheetState.onColumnMoved}
        onColumnResized={bulkSheetState.onColumnResized}
        onColumnVisible={bulkSheetState.onColumnVisible}
        onFilterChanged={bulkSheetState.onFilterChanged}
        onCellValueChanged={() => {
          // valueSetterはrowData配列内のオブジェクトを直接書き換えるだけなので、
          // handleDataChangeを呼んで未保存判定(hasUnsavedChanges)を再計算させる
          handleDataChange(rowData)
        }}
        onRowDragEnter={dragStyle.onRowDragEnter}
        onRowDragMove={dragStyle.onRowDragMove}
        onRowDragEnd={onRowDragEnd}
        onRowDragLeave={dragStyle.refreshDragStyle}
        getContextMenuItems={getContextMenuItems}
      />
      <ColumnSettingPopper
        open={columnSetting.isOpen}
        anchorEl={columnSetting.anchorEl}
        close={columnSetting.close}
        gridApi={gridApiRef.current}
        height={ref.current?.offsetHeight}
        initializeColumnState={() => {
          // 列状態 / フィルタ / ソートを一括初期化する。ag-grid の reset*** は
          // 対応イベントを発火しないため、保存済み state と派生 state を明示的にクリアする。
          gridApiRef.current?.resetColumnState()
          gridApiRef.current?.setFilterModel(null)
          bulkSheetState.saveColumnState([])
          bulkSheetState.saveFilterState({})
          bulkSheetState.resetDerivedState()
        }}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.SubStatusColumnAndFilterState}
        columnState={bulkSheetState.columnState}
        offset={90}
        disabledSaveUIState={true}
      />
      {showCancelDialog && (
        <CancelConfirmDialog
          open={true}
          onConfirm={() => {
            setShowCancelDialog(false)
            handleReload()
          }}
          onClose={() => setShowCancelDialog(false)}
        />
      )}
      <AddRowCountInputDialog {...addRowCountDialogState} />
      {deleteTarget && (
        <DeleteRowConfirmationDialog
          target={deleteTarget.names}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(undefined)}
        />
      )}
      <Loading isLoading={loading} elem={ref.current} />
    </PageArea>
  )
})

export const SubStatus = connect()(SubStatusComponent)
